import { FormFieldErrors } from "../models/FormFieldErrors";

export class FormValidator {
    private fieldName: string;
    private fieldLabel: string | null;
    private fieldValue: any;
    private formFieldErrors: FormFieldErrors;

    constructor(fieldName: string, fieldValue: any, fieldLabel?: string) {
        this.fieldName = fieldName;
        this.fieldValue = fieldValue;
        this.fieldLabel = fieldLabel!;
        this.initialize();
    }

    private initialize(): void {
        this.formFieldErrors = {
            Field: this.fieldName,
            Errors: []
        };
    }

    private getFieldLabel(): string {
        if (this.fieldLabel != null) {
            return this.fieldLabel;
        }
        return this.fieldName;
    }

    required(required: boolean = true): FormValidator {
        if (required) {
            if (this.fieldValue === undefined || this.fieldValue === null || this.fieldValue === "") {
                this.formFieldErrors.Errors.push(this.getFieldLabel() + " is required");
            }
        }
        return this;
    }

    repeated(values: any[]): FormValidator {
        if (values.filter(v => v === this.fieldValue && v !== null && v !== "").length >= 2) {
            this.formFieldErrors.Errors.push(this.getFieldLabel() + " is repeated");
        }
        return this;
    }

    maxLength(maxLength: number): FormValidator {
        if (this.fieldValue?.length > maxLength) {
            this.formFieldErrors.Errors.push(this.getFieldLabel() + " must be less than " + maxLength);
        }
        return this;
    }

    match(value: any, isMatch: boolean = true) {
        const isEmpty = this.fieldValue === undefined || this.fieldValue === null || this.fieldValue === "";
        if (isMatch && !isEmpty && this.fieldValue !== value) {
            this.formFieldErrors.Errors.push(this.getFieldLabel() + " should match password");
        } else if (!isMatch && !isEmpty && this.fieldValue === value) {
            this.formFieldErrors.Errors.push(this.getFieldLabel() + " should not match current password");
        }
        return this;
    }

    customError(showWrror: boolean = false, customMessage: any) {
        if (showWrror) {
            this.formFieldErrors.Errors.push(customMessage);
        }
        return this;
    }

    sameAs(value: any) {
        // if it's Empty, then required validation will be fired, So no need to show this error too
        const isEmpty = this.fieldValue === undefined || this.fieldValue === null || this.fieldValue === "";
        if (this.fieldValue !== value && !isEmpty) {
            this.formFieldErrors.Errors.push(this.getFieldLabel() + " should match password");
        }
        return this;
    }

    email(): FormValidator {
        const regex = RegExp(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/);
        if (this.fieldValue !== undefined && this.fieldValue !== null && this.fieldValue !== "") {
            if (!regex.test(this.fieldValue)) {
                this.formFieldErrors.Errors.push("Must be a valid email address");
            }
        }
        return this;
    }

    notContainsSeries(value: any): FormValidator {
        if (value === null || value === undefined) {
            return this;
        }
        let numbersList = this.buildNumbersList(value.toUpperCase());
        this.validateFollowingCharactersSeries(numbersList);
        this.validateRepeatingCharactersSeries(numbersList);
        return this;
    }

    getFormFieldErrors(): FormFieldErrors | null {
        if (this.formFieldErrors.Errors.length === 0) {
            return null;
        }
        return this.formFieldErrors;
    }

    private buildNumbersList(value: any): any {
        let numbersList: any = [];
        for (let i = 0; i < value.length; i++) {
            let character = value.charAt(i);
            if ("0123456789".indexOf(character) !== -1) {
                numbersList.push(Number(character));
            } else {
                numbersList.push(character.charCodeAt(0));
            }
        }
        return numbersList;
    }

    private validateFollowingCharactersSeries(numbersList: any): void {
        let isSeries = this.checkIsSeries(numbersList);
        this.addSeriesErrorMessage(isSeries, this.getFieldLabel() + " should not contain more than 3 following characters");
    }

    private validateRepeatingCharactersSeries(numbersList: any): void {
        let isSeries = this.checkIsSeries(numbersList, true);
        this.addSeriesErrorMessage(isSeries, this.getFieldLabel() + " should not contain more than 3 repeating characters");
    }

    private checkIsSeries(numbersList: any, isSameOperatorCode: boolean = false): boolean {
        if (isSameOperatorCode) {
            return this.isSeries(numbersList, "Same");
        }
        return this.isSeries(numbersList, "+") || this.isSeries(numbersList, "-");
    }

    private addSeriesErrorMessage(isSeries: boolean, errorMessage: string): void {
        if (isSeries) {
            this.formFieldErrors.Errors.push(errorMessage);
        }
    }

    private isSeries(numbersList: any, operatorCode: string): boolean {
        var result = false;
        var seriesNumberCount: number = 0;
        var seriesNumberList: any = [];
        numbersList.forEach((item: any) => {
            var isNotSeriesNumber = false;
            let test2 = this.handleSeries(seriesNumberList, seriesNumberCount, operatorCode, item);
            seriesNumberList = test2.list;
            seriesNumberCount = test2.count;
            isNotSeriesNumber = test2.isNotSeriesNumber;
            if (seriesNumberCount == 3) {
                result = true; return;
            }
            if (isNotSeriesNumber) {
                seriesNumberCount = 0;
                seriesNumberList = [];
                seriesNumberList.push(item);
            }
        });
        return result;
    }

    private handleSeries(list: any, count: number, operatorCode: string, item: any): { list: any, count: number, isNotSeriesNumber: boolean } {
        let isNotSeriesNumber = false;
        if (item <= 9 || ((item >= 65 && item <= 90))) {
            if (list.length == 0) {
                list.push(item);
            } else {
                let operatorCodeCheckPrefix = this.getSeriesOperatorCheckPrefix(operatorCode);
                if (list[list.length - 1] + operatorCodeCheckPrefix == item) {
                    list.push(item);
                    count += 1;
                } else {
                    isNotSeriesNumber = true;
                }
            }
        } else {
            isNotSeriesNumber = true;
        }
        return { list, count, isNotSeriesNumber };
    }

    private getSeriesOperatorCheckPrefix(operatorCode: string): number {
        if (operatorCode == "+") {
            return 1;
        } else if (operatorCode == "-") {
            return -1;
        } else {
            return 0;
        }
    }
}